import { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { debounce, isEqual } from "lodash";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import Insights from "../../Insights";
import NoResults from "../../../Common/NoResults";
import FloatLoader from "../../../Common/FloatLoader";
import StudentInvitesTableBody from "./StudentInvitesTableBody";
import StudentInvitesTableHeader from "./StudentInvitesTableHeader";

import { getColumns } from "./columns";
import { Order } from "../../../../utils/order";
import { ModalTypes } from "../../Students/Utils";
import { SortingType, GetInvitationStatus } from "../Utils";
import ModalContext from "../../../../contexts/ModalContext";
import { interactionEventDataLayer } from "../../../../utils/DataLayers";

import { useTable } from "../../../../redux/Table/hooks";
import { useAdvisor } from "../../../../redux/Advisor/hooks";
import { useFetchOrganizationAdvisors } from "../../../Common/Hooks";
import { useOrganizations } from "../../../../redux/Organizations/hooks";
import { useStudentInvites } from "../../../../redux/StudentInvites/hooks";
import { Items, loadState, saveState } from "../../../../redux/storePersist";

import { updateWholeListOption } from "../../../../redux/Table/actions";
import {
  fetchStudentInvites,
  resetStudentInvitesState,
} from "../../../../redux/StudentInvites/actions";

function containsObject(obj, list) {
  for (var x in list) {
    if (list.hasOwnProperty(x) && list[x] === obj) return true;
  }
  return false;
}

function getData(invites) {
  const localInvites = invites || [];

  return localInvites.map((invitation) => ({
    id: invitation.id,
    email: invitation.recipient,
    insertTime: new Date(invitation.insertTime),
    assignedAdvisor: invitation.assignedAdvisor,
    sender: invitation.sender,
    status: GetInvitationStatus(invitation.status),
  }));
}

function StudentInvitesTable() {
  const dispatch = useDispatch();

  const { wholeList } = useTable();
  const { profile } = useAdvisor();
  const { pages, selectedOrganizationId } = useOrganizations();
  const { advisors } = useFetchOrganizationAdvisors(selectedOrganizationId);

  const invitesState = useStudentInvites();
  const modalContext = useContext(ModalContext);

  const pageSize = 20;

  const [search, setSearch] = useState("");
  const [orgId, setOrgId] = useState(null);
  const [invites, setInvites] = useState([]);
  const [doFetch, setDoFetch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentRole, setCurrentRole] = useState(null);
  const [getDataFlag, setGetDataFlag] = useState(false);
  const [inviteFilters, setInvitesFilters] = useState([]);
  const [showInsights, setShowInsights] = useState(false);
  const [insightAction, setInsightAction] = useState(false);
  const [currentOrgCode, setCurrentOrgCode] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filterTagsStatus, setFilterTagsStatus] = useState([]);
  const [filterTagsSender, setFilterTagsSender] = useState([]);
  const [filterTagsAdvisor, setFilterTagsAdvisor] = useState([]);
  const [selectedInvitations, setSelectedInvitations] = useState([]);
  const [orderBy, setOrderBy] = useState({ [SortingType.EMAIL]: Order.asc });
  const [organizationId, setOrganizationId] = useState(selectedOrganizationId);

  const defaultFilters = {
    status: [],
    recipient: null,
    assignedAdvisor: [],
    senders: [],
  };
  const [filters, setFilters] = useState(defaultFilters);

  useEffect(() => {
    if (orgId && getDataFlag)
      interactionEventDataLayer({
        eventName: "page_view",
        params: {
          organization_id: orgId,
          user_id: profile.userId,
          search_input: filters.recipient,
          screen_name: "ManageStudentsInvites",
          count_of_search_results: invitesState?.itemsCount,
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, filters.recipient, getDataFlag]);

  useEffect(() => {
    const organization = pages[0].find(
      (org) => org.organization.id === selectedOrganizationId
    );

    setInvites([]);
    setCurrentPage(1);
    setOrganizationId(selectedOrganizationId);
    setCurrentRole(organization?.role || null);
    setOrgId(organization?.organization?.id || null);
    setCurrentOrgCode(organization?.organization?.code || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationId]);

  useEffect(() => {
    let isSubscribed = true;
    const fetchInvitations = async () => {
      await dispatch(
        fetchStudentInvites({
          orderBy,
          organizationId,
          filter: filters,
          page: { pageSize, pageNumber: currentPage },
        })
      );
      if (isSubscribed) {
        setDoFetch(false);
        setGetDataFlag(true);
      }
    };

    if (doFetch) fetchInvitations();
    return () => {
      isSubscribed = false;
      if (wholeList) dispatch(updateWholeListOption(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doFetch]);

  useEffect(() => {
    if (getDataFlag) {
      if (!invitesState.isLoading) {
        setInvites(getData(invitesState?.pages[currentPage - 1]));
        setGetDataFlag(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitesState.isLoading, invitesState.pages, currentPage, getDataFlag]);

  useEffect(() => {
    const { internalState } = loadState(Items.APP_STATE);
    if (internalState?.filters) {
      setShowInsights(true);
      handlerOnViewInsight(internalState?.filters);
      saveState({ internalState: {} }, Items.APP_STATE);
      return;
    }
    if (internalState?.modal) {
      setShowInsights(true);
      setInsightAction(true);
      handlerOnViewInsight(null, true);
      saveState({ internalState: {} }, Items.APP_STATE);
      return;
    }

    dispatch(resetStudentInvitesState());

    const filters = {
      recipient: search || null,
      status: filterTagsStatus.map((filter) => filter.key),
      assignedAdvisor: filterTagsAdvisor.map((filter) => filter.id),
      senders: filterTagsSender.map((filter) => filter.id),
    };

    if (isEqual(defaultFilters, filters)) {
      setFiltersApplied(false);
      if (showInsights && !insightAction) setShowInsights(false);
    } else setFiltersApplied(true);

    setDoFetch(() => {
      setInvites([]);
      setCurrentPage(1);
      setInvitesFilters([
        ...filterTagsStatus,
        ...filterTagsAdvisor,
        ...filterTagsSender,
      ]);

      setFilters(filters);
      setInsightAction(false);
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterTagsSender, filterTagsStatus, filterTagsAdvisor]);

  const closeModal = useCallback(
    (modalType) => {
      if (modalType) {
        switch (modalType) {
          case ModalTypes.DELETE_INVITE:
          case ModalTypes.INVITE_STUDENT:
          case ModalTypes.RE_SEND_INVITE:
            if (wholeList) dispatch(updateWholeListOption(false));
            setInvites([]);
            setCurrentPage(1);
            setDoFetch(true);
            break;
          default:
            break;
        }
      }
    },
    [dispatch, wholeList]
  );

  useEffect(() => {
    closeModal(modalContext.modalResponse);
  }, [closeModal, modalContext.modalResponse]);

  const assingAdvisors = useMemo(() => {
    const noAdvisor = {
      id: null,
      role: null,
      name: "Unassigned",
    };
    const advisor = {
      id: profile.id,
      role: currentRole,
      name: `${profile.firstName} ${profile.lastName}`,
    };

    const sortedAdvisors = [advisor, ...advisors].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    return [...sortedAdvisors, noAdvisor];
  }, [profile, advisors, currentRole]);

  const toggleSortingOrderDebounce = useMemo(
    () =>
      debounce((sortigType, fieldOrder) => {
        setDoFetch(() => {
          setInvites([]);
          setCurrentPage(1);
          setOrderBy({ [sortigType]: fieldOrder });
          return true;
        });
      }, 500),
    []
  );

  const toggleSortingOrder = useCallback(toggleSortingOrderDebounce, [
    toggleSortingOrderDebounce,
  ]);

  const handlerOnSelectInvitations = useCallback(
    (selectedInvitations) => setSelectedInvitations(selectedInvitations),
    []
  );

  const handlerOnChangeSearchDebounce = useMemo(
    () => debounce((value) => setSearch(value), 500),
    []
  );

  const handlerOnChangeSearch = useCallback(handlerOnChangeSearchDebounce, [
    handlerOnChangeSearchDebounce,
  ]);

  const handlerOnRemoveFilterTag = (filter) => {
    setFilterTagsSender(filterTagsSender.filter((x) => x.key !== filter.key));
    setFilterTagsStatus(filterTagsStatus.filter((x) => x.key !== filter.key));
    setFilterTagsAdvisor(filterTagsAdvisor.filter((x) => x.key !== filter.key));
  };

  const handleOnPageChange = (page) => {
    setCurrentPage(page);
    setDoFetch(true);
  };

  const columns = useMemo(
    () =>
      getColumns({
        orderBy,
        assingAdvisors,
        toggleSortingOrder,
        onFilterStatusSelect: (filter) => {
          if (!containsObject(filter, filterTagsStatus)) {
            setFilterTagsStatus([...filterTagsStatus, filter]);
          }
        },
        onFilterAdvisorSelect: (filter) => {
          const mappedFilters = filterTagsAdvisor.map((filter) => filter.key);
          if (!mappedFilters.includes(filter.key)) {
            setFilterTagsAdvisor([...filterTagsAdvisor, filter]);
          }
        },
        onFilterSenderSelect: (filter) => {
          const mappedFilters = filterTagsSender.map((filter) => filter.key);
          if (!mappedFilters.includes(filter.key)) {
            setFilterTagsSender([...filterTagsSender, filter]);
          }
        },
      }),
    [
      orderBy,
      assingAdvisors,
      filterTagsStatus,
      filterTagsAdvisor,
      filterTagsSender,
      toggleSortingOrder,
    ]
  );

  const onClearAllFilters = () => {
    setSearch("");
    setFilterTagsStatus([]);
    setFilterTagsSender([]);
    setFilterTagsAdvisor([]);

    if (showInsights && !insightAction) setShowInsights(false);
  };

  const handlerOnViewInsight = (filterTagsStatus, modal) => {
    setInsightAction(true);
    onClearAllFilters();

    if (!modal) setFilterTagsStatus(filterTagsStatus);
    else
      modalContext.openModalHandler(
        { assingAdvisors, organizationId },
        ModalTypes.INVITE_STUDENT
      );
  };

  const onStudentError = (error) => {
    toast.error(error, {
      progress: 1,
      theme: "colored",
      closeOnClick: true,
      position: "top-right",
      hideProgressBar: false,
    });
  };

  if (invitesState.error) onStudentError(invitesState.error);

  return (
    <div className="px-4 pt-8 relative min-h-screen-footer-md">
      <Insights
        profile={profile}
        show={showInsights}
        invitesInsights={true}
        organizationId={organizationId}
        modalResponse={modalContext.modalResponse}
        handlerOnViewInsight={handlerOnViewInsight}
      />
      <StudentInvitesTableHeader
        filters={filters}
        inviteSearch={search}
        onClear={onClearAllFilters}
        invitesState={invitesState}
        inviteFilters={inviteFilters}
        assingAdvisors={assingAdvisors}
        organizationId={organizationId}
        currentOrgCode={currentOrgCode}
        onChange={handlerOnChangeSearch}
        onRemove={handlerOnRemoveFilterTag}
        selectedInvitations={selectedInvitations}
        openModal={modalContext.openModalHandler}
      />
      <StudentInvitesTableBody
        data={invites}
        columns={columns}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={handleOnPageChange}
        itemsCount={invitesState.itemsCount}
        handlerOnSelectStudents={handlerOnSelectInvitations}
      />
      {inviteFilters.isLoading && (
        <FloatLoader show={inviteFilters.isLoading} />
      )}
      {invites.length === 0 && filtersApplied && !inviteFilters.isLoading && (
        <NoResults
          trigger={filters}
          tableSelector="viewport-student-invites-table"
        />
      )}
    </div>
  );
}

export default StudentInvitesTable;
